import type { HaftkhabisMove, HaftkhabisView } from "@bing/engine-haftkhabis";
import { useState, type ReactNode } from "react";
import { fa } from "../i18n/fa";
import { HaftkhabisTable } from "../screens/HaftkhabisTable";
import { useSession } from "../store/context";
import { HAFTKHABIS_END_MARGIN_MS, WinOverlay } from "./HaftkhabisGame";
import type { LobbyPlayer } from "./online-connection";
import { OnlineGame } from "./OnlineGame";
import type { SeatPlayer } from "./seat-identity";
import { END_REVEAL_DELAY_MS } from "./use-end-reveal";

/** The lobby roster as the felt and the result card both want it: ONE mapping, so a chair is never a
 *  named person on the table and a nameless «بات N» on the end card. */
function seatPlayers(
  players: readonly LobbyPlayer[] | undefined,
): readonly SeatPlayer[] | undefined {
  return players?.map((p) => ({
    seat: p.seat,
    isBot: p.isBot,
    nickname: p.name,
    avatarId: p.user?.avatarId,
    avatarPhotoId: p.user?.avatarPhotoId,
    isOfficial: p.user?.isOfficial,
  }));
}

/** Online هفت خبیث: a thin wrapper over the shared `OnlineGame` shell (`-private` is derived from the room's name). */
export function HaftkhabisOnlineGame(): ReactNode {
  const profile = useSession((s) => s.profile);
  // a re-join (a reload, the strip's «ادامه»): a table in progress, whatever its first frame looks like
  const resumed = useSession((s) => s.onlineJoin.kind === "rejoin");
  // the round's card on the felt: the end card waits for it, then a short margin (پاسور's way)
  const [holding, setHolding] = useState(false);
  return (
    <OnlineGame<HaftkhabisMove, HaftkhabisView>
      gameId="haftkhabis"
      room="haftkhabis-online"
      title={fa.haftkhabis.title}
      endRevealHeld={holding}
      endRevealMs={(v) => (v.phase === "gameOver" ? HAFTKHABIS_END_MARGIN_MS : END_REVEAL_DELAY_MS)}
      renderTable={({ view, legal, send, leave, players, timer, onInviteSeat, seatNotices }) => (
        <HaftkhabisTable
          seatNotices={seatNotices}
          view={view}
          legal={legal}
          onMove={send}
          onLeave={leave}
          self={profile}
          onHolding={setHolding}
          resumed={resumed}
          timer={timer}
          onInviteSeat={onInviteSeat}
          chat
          players={seatPlayers(players)}
        />
      )}
      renderWin={({
        result,
        view,
        again,
        leave,
        players,
        againDisabled,
        againHint,
        againHidden,
      }) => (
        <WinOverlay
          result={result}
          totalScores={view.totalScores}
          targetScore={view.targetScore}
          you={view.you}
          lastRound={view.lastRound}
          self={profile}
          players={seatPlayers(players)}
          onAgain={again}
          againDisabled={againDisabled}
          againHint={againHint}
          againHidden={againHidden}
          onLeave={leave}
        />
      )}
    />
  );
}
